/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Tabs } from '@mui/material';

import { getMarketplaceTabs } from '../../Models/constants';
import { getStoredCustomerDetails, hasAccessToTab, getAuthData } from '../../../../Shared/Utilities/utils';

import { ITabPanelProps } from '../../Models/Marketplace.model';
import CustomerApiService from '../../../Customer/Services/CustomerService';

import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import GlobalSearchBar from '../../../../Shared/Components/Common/GlobalSearchBar/GlobalSearchBar';
import { USER_PROCESSES, UserType, velocitiOrganizationId } from '../../../../Shared/Constants/App';

import { CustomerContainer } from '../../../../Shared/Components/Layout/styles';
import { TabSection, TabTitle } from '../../../Jobs/Components/JobDetailsOverView/JobDetailsOverview.styles';
import { Loader, TabsWrapper } from '../../../Jobs/Components/JobView/JobsView.styles';

function a11yProps(index: number) {
  return {
    id: `marketplace-tab-${index}`,
    'aria-controls': `marketplace-tabpanel-${index}`,
  };
}

function TabPanel(props: ITabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`marketplace-tabpanel-${index}`}
      aria-labelledby={`marketplace-tab-${index}`}
      {...other}>
      {value === index && children}
    </div>
  );
}

const MarketplacePage = (): JSX.Element => {
  const marketplaceTabs = getMarketplaceTabs();
  const { t } = useTranslation(['vendor', 'tableHeader', 'altTexts', 'vendorBidCreation', 'jobAward']);
  const location = useLocation();
  const pathVariables = location?.pathname.split('/');
  const tabIndex = marketplaceTabs.findIndex((tab) => tab.route === pathVariables[pathVariables.length - 1]);
  const [tabSelected, setTabSelected] = useState(tabIndex);
  const { customerId = '', userId } = getStoredCustomerDetails() || {};
  const [processEvents, setProcessEvents] = useState<any[]>([]);
  const [isProcessEventsLoading, setIsProcessEventsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { role } = getAuthData();
  const isCustomerOrNonVelocitiUsersLoggedIn =
    role === UserType.Customer || (role === UserType.Vendor && customerId !== velocitiOrganizationId);

  const handleChangeTabs = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabSelected(newValue);
    // Each tab searches its own data set, so the term does not carry across tabs.
    setSearchText('');
    window.history.pushState(null, 'Peazy', `/marketplace/${marketplaceTabs[newValue].route}`);
  }, []);

  const fetchProcessEvents = async () => {
    try {
      setIsProcessEventsLoading(true);

      if (userId && customerId) {
        const marketPlaceProcessEvents = await CustomerApiService.fetchProcessEvents(
          userId,
          customerId,
          Number(USER_PROCESSES.JobMarketplace)
        );
        const jobBiddingProcessEvents = await CustomerApiService.fetchProcessEvents(
          userId,
          customerId,
          Number(USER_PROCESSES.JobBidding)
        );
        const allProcessEvents = [...(marketPlaceProcessEvents?.data || []), ...(jobBiddingProcessEvents?.data || [])];
        localStorage.setItem('marketplaceProcessEvents', JSON.stringify(allProcessEvents));
        setProcessEvents(allProcessEvents);
      }
      setIsProcessEventsLoading(false);
    } catch (error) {
      console.error('Error fetching process events:', error);
    } finally {
      setIsProcessEventsLoading(false);
    }
  };

  const filteredTabs = !isCustomerOrNonVelocitiUsersLoggedIn
    ? marketplaceTabs?.filter((tab) => hasAccessToTab(processEvents, tab.route))
    : marketplaceTabs;

  useEffect(() => {
    setTabSelected(tabIndex);
  }, [location.pathname]);

  useEffect(() => {
    if (!isCustomerOrNonVelocitiUsersLoggedIn) {
      fetchProcessEvents();
    }
  }, []);

  return (
    <>
      <PageTitle title={t('vendor:marketplace')} />
      <CustomerContainer>
        {isProcessEventsLoading && (
          <Loader>
            <CircularProgress style={{ marginTop: '2rem', color: '#006B6B' }} />
          </Loader>
        )}
        {!isProcessEventsLoading && (
          <TabSection>
            <TabsWrapper>
              <Tabs value={tabSelected} onChange={handleChangeTabs} aria-label="basic tabs">
                {filteredTabs.map((tab, index) => (
                  <TabTitle key={tab.label + index} label={tab.label} {...a11yProps(index)} />
                ))}
              </Tabs>
            </TabsWrapper>
            {filteredTabs.map((tab, index) => (
              <TabPanel value={tabSelected} index={index} key={tab.label}>
                <GlobalSearchBar value={searchText} onSearch={setSearchText} />
                <tab.component searchText={searchText} />
              </TabPanel>
            ))}
          </TabSection>
        )}
      </CustomerContainer>
    </>
  );
};

export default MarketplacePage;
